import { useCallback } from "react";

import {
  useUpdateUserConfig,
  useUserConfig,
} from "@/shared/domain/config/hooks";

import {
  DATE_TIME_FORMAT_CONFIG_KEY,
  DateTimeFormatPreference,
  FormatOptions,
  normalizePreference,
} from "./format";
import {
  CLOCK_OPTIONS,
  ClockOption,
  DATE_PRESET_OPTIONS,
  DatePresetOption,
  formatSample,
} from "./preferenceOptions";

interface UseDateTimeFormatPreferenceResult {
  /** The user's current preference, normalized (defaults applied). */
  preference: DateTimeFormatPreference;
  /** True while the initial config value is loading. */
  isLoading: boolean;
  /** Persist a new preference. Optimistically updates the config cache, which
   *  the provider observes and pushes into the formatting registry. */
  savePreference: (preference: DateTimeFormatPreference) => Promise<void>;
  /** True while a save is in flight. */
  isSaving: boolean;
  /** Every date preset, in display order, with the label to render for it. */
  datePresetOptions: readonly DatePresetOption[];
  /** Every clock convention, in display order, with the label to render. */
  clockOptions: readonly ClockOption[];
  /**
   * Render a fixed sample timestamp under an arbitrary preference — the
   * per-preset examples and the live preview of an unsaved draft.
   */
  formatSample: (
    preference: DateTimeFormatPreference,
    options?: FormatOptions,
  ) => string;
}

/**
 * Read/write hook for the date/time format preference: everything the settings
 * UI needs, and the only supported way to reach it. Reads via the shared
 * `userConfig` query (so it stays in sync with the
 * {@link DateTimeFormatProvider}) and writes via the config mutation; the
 * option lists and `formatSample` come along so the UI never has to know the
 * preset table, the config key, or how a preference resolves into a formatter.
 */
export function useDateTimeFormatPreference(): UseDateTimeFormatPreferenceResult {
  const { data, isLoading } = useUserConfig<DateTimeFormatPreference>(
    DATE_TIME_FORMAT_CONFIG_KEY,
  );
  const { mutateAsync, isPending } = useUpdateUserConfig();

  const savePreference = useCallback(
    async (preference: DateTimeFormatPreference) => {
      await mutateAsync({
        key: DATE_TIME_FORMAT_CONFIG_KEY,
        value: preference,
      });
    },
    [mutateAsync],
  );

  return {
    preference: normalizePreference(data ?? null),
    isLoading,
    savePreference,
    isSaving: isPending,
    datePresetOptions: DATE_PRESET_OPTIONS,
    clockOptions: CLOCK_OPTIONS,
    formatSample,
  };
}
